import * as React from "react";

import Input from "@common/designSystem/Input";
import { ErrorMessage as GlobalErrorMessage } from "@components/account/common";
import GoogleAuth from "@editor/components/account/GoogleAuth";
import Avatar from "@editor/components/common/designSystem/Avatar";
import LabeledControl from "@editor/components/common/designSystem/LabeledControl";
import useCurrentUser from "@editor/hooks/useCurrentUser";

import { useProfileForm } from "@/editor/hooks/useProfileForm";
import Button from "@replo/design-system/components/button/Button";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import { CircleCheck } from "lucide-react";

export default function ProfileBoard() {
  const { user, isLoading: isUserLoading } = useCurrentUser();

  const {
    registerName,
    handleNameSubmit,
    nameErrors,
    isUpdatingName,
    googleErrorMessage,
    handleGoogleConnect,
  } = useProfileForm({ userGetQueryArgs: {} });

  return user && !isUserLoading ? (
    <div className="flex">
      <Avatar name={user.name} size="xl" className="mr-8" />
      <div className="flex flex-1 flex-col">
        <div className="flex w-64 flex-col gap-2">
          <h3 className="text-default text-base font-medium">Your Profile</h3>
          <form
            onSubmit={(e) => void handleNameSubmit(e)}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-2 gap-2">
              <LabeledControl
                label="First Name"
                error={nameErrors?.firstName?.message}
              >
                <Input
                  validityState={nameErrors.firstName ? "invalid" : undefined}
                  autoComplete="off"
                  placeholder="First Name"
                  {...registerName("firstName")}
                  type="text"
                  size="base"
                />
              </LabeledControl>
              <LabeledControl
                label="Last Name"
                error={nameErrors?.lastName?.message}
              >
                <Input
                  // TODO (Chance 2023-11-10): This field should be marked as
                  // required. I assume it's not for stylistic purposes but we
                  // can do this properly while still controlling the style of
                  // the displayed errors.
                  validityState={nameErrors?.lastName ? "invalid" : undefined}
                  autoComplete="off"
                  placeholder="Last Name"
                  {...registerName("lastName")}
                  type="text"
                  size="base"
                />
              </LabeledControl>
            </div>
            <div className="flex flex-col gap-4">
              <LabeledControl label="Email">
                <div className="text-default flex items-center text-base">
                  {user.email}
                  {user.verifiedAt && (
                    <Tooltip content="Email account is verified" triggerAsChild>
                      <div tabIndex={0}>
                        <CircleCheck size={14} className="text-success ml-1" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </LabeledControl>
              {user.referralCode?.code && (
                <LabeledControl label="Referrer Code">
                  <div className="text-default text-base">
                    {user.referralCode.code}
                  </div>
                </LabeledControl>
              )}
              {!user.verifiedAt && (
                <div className="text-muted text-sm">
                  Connect your Google account
                  <GlobalErrorMessage errorMessage={googleErrorMessage} />
                  <GoogleAuth
                    width={256}
                    handleGoogleCredentials={handleGoogleConnect}
                  />
                </div>
              )}
              <div className="text-muted text-sm">
                To change your email address, please reach out to
                <a href="mailto:support@replo.app"> support@replo.app</a>.
              </div>
              <Button
                variant="primary"
                size="base"
                type="submit"
                disabled={isUpdatingName}
                isLoading={isUpdatingName}
              >
                Update Profile
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  ) : null;
}
